/* eslint-disable no-prototype-builtins */
import { isArray } from 'lodash';
import { useCallback, useState } from 'react';
import { handleTablePreferences, handleTablePreferencesEdit, handleTablePreferencesDelete, setTablePreferenceLoading } from 'src/redux/slices/tablePreferences';
import { dispatch, useSelector } from 'src/redux/store';
import { getCurrentTableData, applyColumnOrder, swapColumnsInPlace } from 'src/utils/tablePreferences';
import useGetCurrentUserId from './useGetCurrentUserId';
import useResponsive from './use-responsive';

const MUI_INTERNAL_FIELDS = ['__check__', '__reorder__', '__detail_panel_toggle__'];

const useColumnVisibilityChange = (tableTitle, defaultHidden, { defaultCombinedName = true, defaultCombinedContact = false } = {}) => {
    const userId = useGetCurrentUserId();
    const { tablePreferences: { results }, globalTablePreferences } = useSelector((state) => state.tablePreferences);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        let base = { __check__: true, __detail_panel_toggle__: false, ...(defaultHidden || {}) };
        if (defaultCombinedName) {
            base = { ...base, lead_name: true, first_name: false, last_name: false };
        }
        if (defaultCombinedContact) {
            base = { ...base, contact_details: true, email: false, phone: false };
        }
        return base;
    });
    const isMobile = useResponsive('down', 'sm');
    const [pinnedColumns, setPinnedColumns] = useState({
        right: isMobile ? ['actions'] : ['contact', 'viewDetails', 'actions']
    });
    const [isCombinedName, setIsCombinedName] = useState(defaultCombinedName);
    const [isCombinedContact, setIsCombinedContact] = useState(defaultCombinedContact);
    const [isEnforcedByPractice, setIsEnforcedByPractice] = useState(false);
    const handleColumnVisibilityChange = (newColumns, i) => {
        // Apply locally FIRST so the controlled DataGrid reflects the toggle
        // immediately. Without this, MUI fires onColumnVisibilityModelChange
        // but the parent never updates state — the prop stays stale and the
        // checkbox click visually does nothing. The API roundtrip below then
        // re-saves the stale state, locking the user into their starting view.
        setColumnVisibilityModel(newColumns);
        const unVisibleColumns = Object.entries(newColumns)?.filter(([key, value]) => !value && !MUI_INTERNAL_FIELDS.includes(key))?.map(([key]) => key);
        const allColumns = i?.api?.getAllColumns()?.map((ele) => ele?.field)?.filter((f) => !MUI_INTERNAL_FIELDS.includes(f));
        const visibleColumns = allColumns?.filter((ele) => !unVisibleColumns.includes(ele));
        dispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        let payload = {
            body: { table_name: tableTitle, preferences: { ...currentPreferences, columns: visibleColumns } },
            practiceUserId: userId
        };
        if (isArray(tableData) && tableData.length > 0) {
            payload = { ...payload, tableId: tableData[0].id };
            dispatch(handleTablePreferencesEdit(payload));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    };
    const handleColumnOrderChange = (newColumns) => {
        dispatch(setTablePreferenceLoading(true));
        const filteredColumns = newColumns?.filter((f) => !MUI_INTERNAL_FIELDS.includes(f));
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        let payload = {
            body: { table_name: tableTitle, preferences: { ...currentPreferences, columns: filteredColumns } },
            practiceUserId: userId
        };
        if (isArray(tableData) && tableData.length > 0) {
            payload = { ...payload, tableId: tableData[0].id };
            dispatch(handleTablePreferencesEdit(payload));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    };

    const resetColumns = useCallback(() => {
        const defaultPinned = {
            left: [],
            right: isMobile ? ['actions'] : ['contact', 'viewDetails', 'actions']
        };
        setPinnedColumns(defaultPinned);

        let resetVisibility = { __check__: true, __detail_panel_toggle__: false, ...(defaultHidden || {}) };
        if (defaultCombinedName) {
            resetVisibility = { ...resetVisibility, lead_name: true, first_name: false, last_name: false };
        }
        if (defaultCombinedContact) {
            resetVisibility = { ...resetVisibility, contact_details: true, email: false, phone: false };
        }
        setColumnVisibilityModel(resetVisibility);
        setIsCombinedName(defaultCombinedName);
        setIsCombinedContact(defaultCombinedContact);

        // DELETE the user's preference record so the cascade falls through to practice defaults
        const tableData = getCurrentTableData(results, tableTitle);
        if (isArray(tableData) && tableData.length > 0 && tableData[0].id) {
            dispatch(setTablePreferenceLoading(true));
            dispatch(handleTablePreferencesDelete({
                practiceUserId: userId,
                tableId: tableData[0].id,
                tableName: tableTitle,
            }));
        }
    }, [isMobile, userId, tableTitle, setPinnedColumns, defaultHidden, defaultCombinedName, defaultCombinedContact, results]);

    // handleToggleCombineName/handleToggleCombineContact accept an optional
    // second `{ apiRef }` arg (NewLeadDataGrid.jsx is the only place that has
    // one — this hook is called from the PAGE level, above where apiRef is
    // created). Without it: (1) the saved `columns` order list is rewritten
    // in place (combined field takes the first split field's slot) so the
    // NEXT handleTablePreference pass (triggered once the save round-trips
    // back through Redux) repositions it correctly instead of leaving it
    // wherever MUI DataGridPro appends a column it has never seen in the live
    // order before (the end). With `apiRef` passed: the same reorder is also
    // applied immediately, so the user sees the column move without waiting
    // for that round-trip or a reload.
    const handleToggleCombineName = useCallback((value, { apiRef } = {}) => {
        setIsCombinedName(value);
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const baseColumns = Array.isArray(currentPreferences.columns) && currentPreferences.columns.length > 0
            ? currentPreferences.columns
            : (apiRef?.current?.getAllColumns?.()?.map((c) => c.field) || []);
        const nextColumns = swapColumnsInPlace(baseColumns, { combinedField: 'lead_name', splitFields: ['first_name', 'last_name'] }, value);

        setColumnVisibilityModel(prev => ({
            ...prev,
            lead_name: value,
            first_name: !value,
            last_name: !value,
        }));
        applyColumnOrder(apiRef, nextColumns);

        dispatch(setTablePreferenceLoading(true));
        let payload = {
            body: { table_name: tableTitle, preferences: { ...currentPreferences, isCombinedName: value, ...(nextColumns.length > 0 ? { columns: nextColumns } : {}) } },
            practiceUserId: userId
        };
        if (isArray(tableData) && tableData.length > 0) {
            payload = { ...payload, tableId: tableData[0].id };
            dispatch(handleTablePreferencesEdit(payload));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [results, globalTablePreferences, tableTitle, userId]);

    const handleToggleCombineContact = useCallback((value, { apiRef } = {}) => {
        setIsCombinedContact(value);
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const baseColumns = Array.isArray(currentPreferences.columns) && currentPreferences.columns.length > 0
            ? currentPreferences.columns
            : (apiRef?.current?.getAllColumns?.()?.map((c) => c.field) || []);
        const nextColumns = swapColumnsInPlace(baseColumns, { combinedField: 'contact_details', splitFields: ['email', 'phone'] }, value);

        setColumnVisibilityModel(prev => ({
            ...prev,
            contact_details: value,
            email: !value,
            phone: !value,
        }));
        applyColumnOrder(apiRef, nextColumns);

        dispatch(setTablePreferenceLoading(true));
        let payload = {
            body: { table_name: tableTitle, preferences: { ...currentPreferences, isCombinedContact: value, ...(nextColumns.length > 0 ? { columns: nextColumns } : {}) } },
            practiceUserId: userId
        };
        if (isArray(tableData) && tableData.length > 0) {
            payload = { ...payload, tableId: tableData[0].id };
            dispatch(handleTablePreferencesEdit(payload));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [results, globalTablePreferences, tableTitle, userId]);

    return { userId, columnVisibilityModel, handleColumnVisibilityChange, setColumnVisibilityModel, handleColumnOrderChange, pinnedColumns, setPinnedColumns, resetColumns, isCombinedName, setIsCombinedName, handleToggleCombineName, isCombinedContact, setIsCombinedContact, handleToggleCombineContact, isEnforcedByPractice, setIsEnforcedByPractice };
};

export default useColumnVisibilityChange;
